'use client';

import { Check } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';

import { SelectValues } from './select-values';
import { SelectLoading } from './SelectLoading';
import { SelectOption } from './types';

export interface SelectProps<TValue, TMultiple extends boolean = false> {
  // 数据相关
  options: SelectOption<TValue>[];
  multiple?: TMultiple;

  // Select 特有的属性
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  loading?: boolean;
  loadingText?: string;
  allowReselectClear?: boolean;
  tagRender?: TMultiple extends true
    ? (option: SelectOption<TValue>, onRemove: () => void) => React.ReactNode
    : never;

  // 显示相关
  getDisplayValue?: (option: SelectOption<TValue>) => string;

  // 选择相关
  value?: TMultiple extends true ? TValue[] : TValue | null;
  onChange?: (value: TMultiple extends true ? TValue[] : TValue | null) => void;

  // 事件
  onClear?: () => void;
  onDropdownVisibleChange?: (visible: boolean) => void;

  // 样式
  emptyText?: string;
}

export function Select<TValue, TMultiple extends boolean = false>({
  // Select 属性
  options = [],
  placeholder = '请选择',
  searchPlaceholder = '搜索选项...',
  className,
  disabled,
  clearable = true,
  searchable = true,
  loading,
  loadingText = '加载中...',
  allowReselectClear = true,
  tagRender,
  getDisplayValue,
  onClear,
  onDropdownVisibleChange,
  emptyText = '未找到选项',

  // 选择相关
  value,
  onChange,
  multiple,
}: SelectProps<TValue, TMultiple>) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  // 获取选中的选项
  const selectedOptions = useMemo(() => {
    const selectedKeys = multiple
      ? (value as TValue[]) || []
      : typeof value === 'boolean' || value
        ? [value as TValue]
        : [];
    return options.filter((option) => selectedKeys.includes(option.value));
  }, [options, value, multiple]);

  // 处理清空
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (multiple) {
        onChange?.([] as any);
      } else {
        onChange?.(null as any);
      }
      onClear?.();
    },
    [multiple, onChange, onClear]
  );

  // 处理移除单个标签
  const handleRemoveTag = useCallback(
    (nodeKey: string) => {
      if (multiple) {
        const newSelection = selectedOptions.filter((o) => o.key !== nodeKey);
        onChange?.(
          newSelection.map((o) => o.value) as TMultiple extends true ? TValue[] : TValue | null
        );
      }
    },
    [multiple, selectedOptions, onChange]
  );

  // 处理 popover 开关
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (disabled) {
        return;
      }

      setOpen(newOpen);
      onDropdownVisibleChange?.(newOpen);
    },
    [disabled, onDropdownVisibleChange]
  );

  // 处理选项点击
  const handleOptionSelect = useCallback(
    (optionValue: TValue) => {
      if (disabled) {
        return;
      }

      if (multiple) {
        const currentArray = (Array.isArray(value) ? value : []) as TValue[];
        const newSelection = currentArray.includes(optionValue)
          ? currentArray.filter((v) => v !== optionValue)
          : [...currentArray, optionValue];
        onChange?.(newSelection as any);
      } else {
        const shouldClear = allowReselectClear && value === optionValue;
        onChange?.((shouldClear ? null : optionValue) as any);
        setOpen(false); // 单选模式下选中后关闭
      }
    },
    [allowReselectClear, disabled, multiple, value, onChange]
  );

  // 判断选项是否被选中
  const isOptionSelected = useCallback(
    (optionValue: TValue) => {
      if (multiple) {
        return Array.isArray(value) && value.includes(optionValue);
      }
      return value === optionValue;
    },
    [multiple, value]
  );

  // Loading 状态下的处理
  if (loading) {
    return <SelectLoading className={className} loadingText={loadingText} />;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTrigger asChild>
        <SelectValues
          selectedOptions={selectedOptions}
          multiple={multiple}
          placeholder={placeholder}
          getDisplayValue={getDisplayValue}
          tagRender={tagRender}
          onRemoveTag={handleRemoveTag}
          disabled={disabled}
          clearable={clearable}
          onClear={handleClear}
          className={className}
          aria-controls="select-content"
          aria-expanded={open}
        />
      </PopoverTrigger>

      <PopoverContent
        id="select-content"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          {searchable && (
            <CommandInput placeholder={searchPlaceholder} name="command-input" autoComplete="off" />
          )}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.key}
                  value={option.label}
                  onSelect={() => handleOptionSelect(option.value)}
                  disabled={option.disabled}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate" title={option.label}>
                      {option.label}
                    </div>
                  </div>
                  {isOptionSelected(option.value) && <Check className="ml-2 h-4 w-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
